import React, { memo, useMemo } from "react";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { Box } from "@material-ui/core";
import { Text } from "common";
import { isDate } from "date-fns";
import { format } from "date-fns-tz";
import { useTimeZoneSupport } from "hooks";
import { Badge } from "custom";

interface IProps {
  instances: any[];
}

const ROW_HEIGHT = 44;
const MAX_HEIGHT = 220;

const Row = memo(({ index, style, data }: ListChildComponentProps) => {
  const { instances, timeZone, dateToTimeZone } = data;
  const item = instances[index];

  const convertedStartDate = dateToTimeZone(item?.startDate);
  const convertedEndDate = dateToTimeZone(item?.endDate);
  const [formatedStartDate, formatedEndDate] = useMemo(
    () => [
      isDate(convertedStartDate)
        ? format(
            convertedStartDate as Date,
            "MMMM d','yyyy 'at' hh:mm:ss' 'aa XX",
            { timeZone },
          )
        : "",
      isDate(convertedEndDate)
        ? format(
            convertedEndDate as Date,
            "MMMM d','yyyy 'at' hh:mm:ss' 'aa XX",
            { timeZone },
          )
        : "",
    ],
    [convertedEndDate, convertedStartDate, timeZone],
  );

  return (
    <Box
      style={style}
      display="flex"
      alignItems="center"
      px="10px"
      py="8px"
      borderBottom="1px solid #eee"
    >
      <Box flex={3} minWidth={0}>
        <Text fontSize="13px" fontWeight={600} noWrap>
          {item.name}
        </Text>
      </Box>
      <Box flex={2}>
        <Text fontWeight={600} fontSize="13px">
          {formatedStartDate}
        </Text>
      </Box>
      <Box flex={2}>
        <Text fontWeight={600} fontSize="13px">
          {formatedEndDate}
        </Text>
      </Box>
      <Box flex={1} display="flex" justifyContent="flex-end" p={0}>
        <Badge
          as={item?.status}
          variant="contained"
          borderRadius="5px"
          width="120px"
          height="26px"
        />
      </Box>
    </Box>
  );
});

const InstancesVirtualList: React.FC<IProps> = ({ instances }) => {
  const { dateToTimeZone, timeZone } = useTimeZoneSupport();

  const itemData = useMemo(
    () => ({
      instances,
      dateToTimeZone,
      timeZone,
    }),
    [instances, dateToTimeZone, timeZone],
  );

  const calculatedHeight = Math.min(
    instances.length * ROW_HEIGHT + 32,
    MAX_HEIGHT,
  );

  return (
    <Box width="100%">
      {/* Header */}
      <Box
        display="flex"
        px="10px"
        py="6px"
        fontSize="14px"
        fontWeight={600}
        borderBottom="1px solid #ddd"
        bgcolor="#fafafa"
      >
        <Box flex={3}>Name</Box>
        <Box flex={2}>Start</Box>
        <Box flex={2}>End</Box>
        <Box flex={1} textAlign="right">
          Status
        </Box>
      </Box>
      <FixedSizeList
        height={calculatedHeight}
        itemCount={instances.length}
        itemSize={ROW_HEIGHT}
        width="100%"
        itemData={itemData}
      >
        {Row}
      </FixedSizeList>
    </Box>
  );
};

export default InstancesVirtualList;
